// ============================================================
// dividers.js — EL SEPARADOR ENTRE DOS SECCIONES (módulo PURO)
//
// Aquí vive TODO lo que decide y dibuja el separador: el catálogo de formas, la
// lista de bloques que nunca lo llevan, y las dos maneras de pintarlo.
//
// POR QUÉ ESTE FICHERO EXISTE (y no sigue dentro de render.js): render.js NO es
// importable fuera del navegador — arrastra store.js y ocho módulos más con DOM —,
// así que ni los tests de node ni ningún otro consumidor podían ejercitar esta
// lógica. Mismo precedente y mismo trato que `core/theme-tokens.js`: el módulo es
// puro (cero imports, cero DOM, solo strings) y render.js RE-EXPORTA los tres
// símbolos históricos para no cambiarle la API a nadie (divider-motion.js,
// divider-studio.js, tools.js y viewer.js los siguen importando de donde estaban).
//
// LOS DOS MODELOS (la razón de ser de este módulo)
// ------------------------------------------------
//  · PARCHE (el de siempre) — un `<svg class="section-divider">` colgado al PIE de
//    la sección A, con sus paths rellenos del color PLANO de la sección B. La cuña
//    que "parece B" en realidad es A pintada del color de B. Funciona mientras el
//    fondo de B sea un color liso; en cuanto B tiene una forma decorativa, un
//    degradado, un fondo animado o una foto, la línea del separador la CORTA
//    (el fondo de B vive dentro de B, con `overflow:hidden`, y nunca llega a la cuña).
//    Ningún z-index arregla eso: es que ese trozo de pantalla no es B.
//  · RECORTE (el de por defecto ahora) — no se pinta nada: se RECORTA el borde.
//    A se recorta por abajo con la forma (`clip-path`), B se mete H px por debajo de A
//    (`margin-top:-H`) y se recorta por arriba con la forma complementaria, y para que
//    su contenido no suba se le mete un HUECO en flujo (`.n3i-recorte-hueco`, alto H).
//    Resultado: la cuña enseña el fondo REAL de B —blob, degradado, animación, foto—
//    y el resto de la banda el fondo REAL de A. La página no cambia de alto y el
//    contenido de las dos secciones se queda donde estaba.
//
// El recorte no puede expresarlo TODO (un color fijo es una franja de un tercer color,
// una opacidad parcial es una mezcla, "Arriba"/animado/parallax mueven o dibujan el
// SVG…). Esos casos siguen con el parche, y quién es quién lo decide UNA sola función:
// `decidirDivider`. Fuera de ella no hay ninguna otra regla escrita sobre el tema.
//
// PARIDAD (invariante nº4 del builder): la inyección la hace `inyectarDivider`, que
// llaman render.js (editor) Y viewer.js::compileSiteHtml (publicado). Ya no son dos
// bloques de código gemelos que se puedan desincronizar: es la MISMA función.
// ============================================================

import { finDelTag } from '../utils/tag.js';

export const DIVIDER_CATALOG = {
    // --- Orgánicos ---
    'wave':          { label: 'Ola',          path: 'M0,40 Q360,80 720,40 Q1080,0 1440,40 L1440,80 L0,80 Z' },
    'wave-smooth':   { label: 'Ola Suave',    path: 'M0,60 C240,20 480,80 720,50 C960,20 1200,70 1440,40 L1440,80 L0,80 Z' },
    'layered-wave':  { label: 'Ola Doble',    paths: [
        { d: 'M0,50 Q360,80 720,45 Q1080,10 1440,50 L1440,80 L0,80 Z', opacity: 0.5 },
        { d: 'M0,55 Q360,25 720,60 Q1080,80 1440,45 L1440,80 L0,80 Z', opacity: 1.0 }
    ]},
    'curve':         { label: 'Curva',        path: 'M0,80 Q720,0 1440,80 Z' },
    'curve-asym':    { label: 'Curva Asim.',  path: 'M0,80 Q400,-10 900,60 Q1200,80 1440,30 L1440,80 Z' },

    // --- Geométricos ---
    'diagonal':      { label: 'Diagonal',     path: 'M0,80 L1440,0 L1440,80 Z' },
    'slant-double':  { label: 'Doble Corte',  paths: [
        { d: 'M0,80 L1440,20 L1440,80 Z', opacity: 0.4 },
        { d: 'M0,80 L1440,40 L1440,80 Z', opacity: 1.0 }
    ]},
    'zigzag':        { label: 'Zigzag',       path: 'M0,80 L120,40 L240,80 L360,40 L480,80 L600,40 L720,80 L840,40 L960,80 L1080,40 L1200,80 L1320,40 L1440,80 Z' },
    'steps':         { label: 'Escalones',    path: 'M0,80 L0,60 L360,60 L360,40 L720,40 L720,20 L1080,20 L1080,0 L1440,0 L1440,80 Z' },
    'arrow':         { label: 'Flecha',       path: 'M0,80 L720,0 L1440,80 Z' },

    // --- Artísticos ---
    'torn-paper':    { label: 'Papel Roto',   path: 'M0,60 L80,55 L130,65 L200,50 L280,62 L350,48 L440,58 L500,45 L580,60 L660,42 L720,55 L800,48 L880,62 L960,44 L1040,58 L1100,50 L1180,63 L1260,47 L1340,56 L1440,52 L1440,80 L0,80 Z' },
    'drops':         { label: 'Gotas',        path: 'M0,80 Q60,60 120,80 Q180,60 240,80 Q300,60 360,80 Q420,60 480,80 Q540,60 600,80 Q660,60 720,80 Q780,60 840,80 Q900,60 960,80 Q1020,60 1080,80 Q1140,60 1200,80 Q1260,60 1320,80 Q1380,60 1440,80 Z' },
    'mountain':      { label: 'Montaña',      path: 'M0,80 L200,30 L400,60 L600,10 L800,50 L1000,20 L1200,55 L1440,25 L1440,80 Z' },

    // --- Track B (visual): formas con carácter (append-only) ---
    'scallop':       { label: 'Festón',       path: 'M0,50 Q90,25 180,50 Q270,25 360,50 Q450,25 540,50 Q630,25 720,50 Q810,25 900,50 Q990,25 1080,50 Q1170,25 1260,50 Q1350,25 1440,50 L1440,80 L0,80 Z' },
    'cloud':         { label: 'Nube',         path: 'M0,50 C36,50 72,18 120,18 C168,18 204,50 240,50 C276,50 312,18 360,18 C408,18 444,50 480,50 C516,50 552,18 600,18 C648,18 684,50 720,50 C756,50 792,18 840,18 C888,18 924,50 960,50 C996,50 1032,18 1080,18 C1128,18 1164,50 1200,50 C1236,50 1272,18 1320,18 C1368,18 1404,50 1440,50 L1440,80 L0,80 Z' },
    'tilt-soft':     { label: 'Inclin. Suave', path: 'M0,65 C360,55 1080,25 1440,15 L1440,80 L0,80 Z' },
    'book':          { label: 'Libro',        path: 'M0,40 Q360,40 600,62 Q660,68 720,68 Q780,68 840,62 Q1080,40 1440,40 L1440,80 L0,80 Z' },
    'triangle-asym': { label: 'Triáng. Asim.', path: 'M0,80 L450,8 L1440,80 Z' },
    'brush':         { label: 'Pincel',       path: 'M0,52 C100,46 180,58 260,50 C300,46 320,55 380,51 C460,46 520,60 600,52 C680,44 720,57 800,50 C880,43 940,58 1020,51 C1100,44 1160,57 1240,50 C1320,44 1380,54 1440,49 L1440,80 L0,80 Z' },
    'fan':           { label: 'Abanico',      path: 'M0,55 Q60,12 120,55 Q180,12 240,55 Q300,12 360,55 Q420,12 480,55 Q540,12 600,55 Q660,12 720,55 Q780,12 840,55 Q900,12 960,55 Q1020,12 1080,55 Q1140,12 1200,55 Q1260,12 1320,55 Q1380,12 1440,55 L1440,80 L0,80 Z' },
    'peak':          { label: 'Picos',        path: 'M0,80 L0,50 L240,15 L480,50 L720,10 L960,50 L1200,20 L1440,50 L1440,80 Z' },

    // --- Track B (visual): dividers con movimiento real (animados por JS, ver divider-motion.js) ---
    // `motion` y `layers` son metadata: la inyección y el divider-studio solo leen d/opacity.
    // Las paths se extienden a x∈[-240,1680] para que el desplazamiento no revele los bordes.
    'wave-parallax': { label: 'Olas Parallax', motion: 'parallax',
        paths: [
            { d: 'M-240,45 Q120,70 480,45 Q840,20 1200,45 Q1560,70 1680,50 L1680,80 L-240,80 Z', opacity: 0.35 },
            { d: 'M-240,50 Q120,28 480,52 Q840,72 1200,50 Q1560,30 1680,48 L1680,80 L-240,80 Z', opacity: 0.6 },
            { d: 'M-240,55 Q120,72 480,52 Q840,32 1200,55 Q1560,74 1680,54 L1680,80 L-240,80 Z', opacity: 1.0 }
        ],
        layers: [
            { baseline: 45, amp: 13, freq: 1.4, speed: 38 },
            { baseline: 50, amp: 17, freq: 2.0, speed: 78 },
            { baseline: 55, amp: 11, freq: 2.7, speed: 128 }
        ]
    },
    'wave-morph':    { label: 'Ola Viva',     motion: 'morph',
        path: 'M-240,48 Q120,70 480,48 Q840,26 1200,48 Q1560,70 1680,48 L1680,80 L-240,80 Z',
        layers: [ { baseline: 48, amp: 16, freq: 1.8, speed: 0.45 } ]
    },
};

/** Retorna array de {d, opacity} — soporta single-path y multi-path entries */
export function getDividerPaths(type) {
    const entry = DIVIDER_CATALOG[type] || DIVIDER_CATALOG.wave;
    return entry.paths || [{ d: entry.path, opacity: 1 }];
}

// Tipos que NUNCA llevan separador de sección. El divider se inyecta al PIE del bloque (antes de
// su etiqueta de cierre, aquí y en viewer.js::compileSiteHtml) y asume un bloque-sección de ancho
// completo: header/footer no lo llevan por jerarquía; cta-bar es una barra fija (.cta-sticky-bar,
// NO <section>) → un divider colgando de su borde se ve roto; ai-custom vive en un iframe sandbox.
// SSOT del front: divider-studio.js re-exporta esta lista como DIVIDER_ELIGIBLE_EXCLUDE (gatea el
// botón), y viewer.js la importa para el guard de compilación. El backend replica la regla en
// llm_sanitizers._fix_motion_props (no comparten literal front/back; se documentan mutuamente).
// `imagen` entra por un motivo DISTINTO al de los otros cuatro (que no emiten <section> o
// viven en un iframe): SÍ emite <section>, pero su contenido es una FOTO a sangre y el SVG
// se inyecta `position:absolute; bottom:-1px` dentro de `.imagen-section{position:relative}`
// → el separador se pintaría ENCIMA de la imagen, no entre dos bandas de color. Espejo en el
// backend: `llm_sanitizers._fix_motion_props` lo fuerza a 'none' para el mismo tipo.
export const DIVIDER_EXCLUDE_TYPES = ['header', 'footer', 'cta-bar', 'ai-custom', 'imagen'];

// Tipos que no pueden ser la sección de ABAJO de un RECORTE (siguen llevando el parche). Dos
// familias, las dos por la misma consecuencia: el recorte les saldría mal, no por gusto.
//   (a) su raíz NO es un `<section>` con altura de contenido: header → <header>, footer →
//       <footer>, cta-bar → <div class="cta-bar-editor">. No hay dónde colgar el `clip-path`
//       ni el `margin-top` negativo, y el hueco tampoco cabe en su layout.
//   (b) su alto es FIJO o MÍNIMO y además centran su contenido (`justify-content:center`):
//       hero y carrusel (`min-height:100vh` / `--carrusel-h`), banner (`min-height:50vh`),
//       video (`min-height:90vh`), ai-custom (iframe con `min-height`) y journey (su variante
//       pinned mide `height:300vh` en `.preview-mode` y su ruta SVG se calcula sobre esa
//       altura, `journey-scroll.js::progressFor`). Meterles el hueco NO las hace crecer —el
//       mínimo ya manda—, así que perderían H px de alto VISIBLE y su contenido se
//       descentraría media banda. Con el parche se ven exactamente igual que hoy.
// Es una BLOCKLIST a propósito (regla práctica de la checklist 5.4 bis): un bloque nuevo, que
// por convención tiene altura de contenido, hereda el recorte gratis.
export const RECORTE_ARRIBA_EXCLUDE = ['header', 'footer', 'cta-bar', 'ai-custom', 'hero', 'carrusel', 'banner', 'video', 'journey'];

// Bloques "de conversión": el separador que los precede es más alto para generar expectación
// (y se marca con su propia clase en el parche). Literal histórico, movido tal cual.
const PRECONVERSION_TYPES = ['pricing', 'cta-bar', 'newsletter'];

// El viewBox en el que están escritas TODAS las formas del catálogo. El SVG se estira con
// `preserveAspectRatio="none"`: x∈[0,1440] → 0-100 % del ancho, y∈[0,80] → 0-H px de la banda.
const VB_W = 1440;
const VB_H = 80;

// ── Cuántos puntos y DÓNDE ────────────────────────────────────────────────────────────────
// Los tramos rectos (L) no se muestrean NUNCA: sus vértices van EXACTOS, y eso es lo que
// mantiene afilados el zigzag, los escalones y las montañas. Lo que sigue es solo para curvas.
//
// Hasta ahora había un presupuesto PLANO (64 puntos) repartido por el ANCHO de cada tramo, y
// eso reparte mal por los dos lados: una Ola son dos Q's que giran 12,7° cada una y se llevaban
// 32 puntos cada una (un vértice cada 0,4°: invisible, y 2,1 KB de `polygon()` por separador),
// mientras que la Nube son 12 curvas que giran 44,6° cada una y se llevaban 5 puntos cada una
// (codos de hasta 17° entre segmento y segmento: ESO es lo que se ve como sierra). MEDIDO sobre
// el catálogo real: la SAGITA —cuánto se separa la polilínea de la curva de verdad— ya era
// SUB-PÍXEL en las 23 formas (0,64 px la peor, el Abanico), así que NO es el criterio que hay
// que perseguir; el criterio es el ÁNGULO.
//
// Hoy el reparto es ADAPTATIVO POR CURVATURA: cada tramo curvo se parte por la mitad mientras
// la cuerda no represente bien su trozo, con DOS tolerancias que se comprueban a la vez:
//   · SAGITA — cuánto se aparta la cuerda de la curva. Acota el error de POSICIÓN.
//   · GIRO   — cuánto se dobla el trozo. Acota el CODO en el vértice, que es lo que se ve.
// Donde el path va casi recto sale a la primera (una cuerda larga y exacta); donde gira, se
// parte. Resultado: las formas suaves bajan de 65 puntos a un puñado (menos HTML en cada
// página) y las que de verdad giran suben, sin pasar del techo.
const TOL_SAGITA = 0.25;          // unidades del viewBox (≈0,19 px con la banda de 60)
const TOL_GIRO = 10;              // grados de giro por vértice (⇒ ángulo mínimo ≈ 170°)
const MUESTRAS_PROF_MAX = 6;      // 2^6 = 64 cuerdas por tramo como techo duro
// Presupuesto de puntos del borde ENTERO (~3,3 KB de `polygon()`). NO es un techo duro, y el
// matiz importa: el adaptativo emite como MÍNIMO un punto por tramo, así que un `custom_path`
// de 130 curvas no baja de 131 puntos por mucho que se afloje la tolerancia (medido: 520 puntos
// / 16 KB antes del respaldo). Lo que sí garantiza `muestrearPath` es no emitir NUNCA más
// puntos que el reparto FIJO con este mismo presupuesto — o sea, nunca más de lo que costaba
// antes del adaptativo. Ver el respaldo al final de la rama adaptativa.
const MUESTRAS_MAX = 104;
// Suelo de muestras por tramo del modo de presupuesto FIJO (`muestrearPath(tramos, n)` con `n`
// explícito): el reparto histórico por ancho, que sigue siendo la manera de pedir una
// referencia densa y uniforme de la curva (la que mide cuánto se aparta de ella el borde real).
const MUESTRAS_MIN_TRAMO = 3;

/** Hueco en flujo que compensa el `margin-top` negativo de la sección de abajo. */
export const RECORTE_HUECO_CLASE = 'n3i-recorte-hueco';
/** El markup del hueco, UNA vez: lo emite la inyección y lo replica la vista previa en vivo
 *  del Divider Studio (si se escribieran por separado podrían dejar de ser el mismo nodo). */
export const RECORTE_HUECO_HTML = '<div class="' + RECORTE_HUECO_CLASE + '" aria-hidden="true"></div>';
const HUECO_HTML = RECORTE_HUECO_HTML;


// ─────────────────────────────────────────────────────────────
//  Saneo de lo que viene de `divider_config` (entrada del cliente)
// ─────────────────────────────────────────────────────────────
// `datos_json_builder` se guarda sin validar valores (ver `attrSafe` en viewer.js), y este
// módulo es AHORA el único pintor del separador para editor Y publicado: lo que interpole en
// un atributo lo escapa, y color/path/altura/opacidad se acotan a lo que un separador puede ser.

/** Escape mínimo para meter un valor dentro de un atributo HTML entre comillas dobles. */
function _attr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
                    .replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Un color CSS plausible (hex, rgb()/hsl(), `var(--accent)`, nombre) o ''. */
function _colorSeguro(c) {
    return (typeof c === 'string' && /^[#\w(),.%\s-]{1,64}$/.test(c)) ? c : '';
}

/** Un `d` de path SVG plausible (solo comandos, números y separadores) o ''. */
function _pathSeguro(d) {
    return (typeof d === 'string' && /^[MmLlHhVvCcSsQqTtAaZz0-9,.\s+-]{1,4000}$/.test(d)) ? d : '';
}

/** Altura de la banda: lo que pide el cliente acotado al rango del slider (20-200), o el
 *  defecto histórico (60; 100 delante de un bloque de conversión). */
function _altura(v, preconversion) {
    const n = Number(v);
    if (v !== null && v !== '' && isFinite(n) && n > 0) return Math.min(200, Math.max(20, Math.round(n)));
    return preconversion ? 100 : 60;
}

/** Opacidad 0-100 (el valor del slider); ausente o basura = 100. */
function _opacidad(v) {
    const n = Number(v);
    if (v === undefined || v === null || v === '' || !isFinite(n)) return 100;
    return Math.min(100, Math.max(0, n));
}


// ─────────────────────────────────────────────────────────────
//  Geometría: del `d` del path a una polilínea que es el BORDE
// ─────────────────────────────────────────────────────────────

function _num(v) {
    const n = parseFloat(v);
    return isFinite(n) ? n : null;
}

/**
 * Parte el atributo `d` de una forma del catálogo (o un `custom_path` del cliente) en tramos.
 *
 * Acepta SOLO comandos ABSOLUTOS M/L/C/Q/Z, que es lo que usa el catálogo entero. Cualquier
 * otra cosa (relativos en minúscula, arcos `A`, atajos `H/V/S/T`, varios subpaths) devuelve
 * `null` → el separador cae al modelo parche, que dibuja el path tal cual sin entenderlo.
 * Se admite la repetición implícita del último comando (`M0,80 L100,0 200,80`), que es SVG legal.
 *
 * @param {string} d
 * @returns {Array<Object>|null} tramos {tipo:'L'|'C'|'Q', p0, p1, c1?, c2?} o null
 */
export function parsearPathDivider(d) {
    if (typeof d !== 'string' || !d.trim()) return null;
    // Cribado barato: si aparece cualquier letra que no sea M/L/C/Q/Z (o la `e` de un
    // exponente), no es un path que sepamos recortar.
    if (/[^MLCQZeE0-9,.\-+\s]/.test(d)) return null;
    const tokens = d.match(/[MLCQZ]|[-+]?[0-9]*\.?[0-9]+(?:[eE][-+]?[0-9]+)?/g);
    if (!tokens) return null;

    const tramos = [];
    let cur = null;
    let mando = null;      // último comando visto (para la repetición implícita)
    let vistos = 0;        // nº de `M`: más de uno = varios subpaths → no sabemos recortarlo
    let i = 0;

    const leer = (n) => {
        const out = [];
        for (let k = 0; k < n; k++) {
            const v = _num(tokens[i]);
            if (v === null) return null;
            out.push(v);
            i++;
        }
        return out;
    };

    while (i < tokens.length) {
        const t = tokens[i];
        if (/^[MLCQZ]$/.test(t)) { mando = t; i++; if (t === 'Z') { continue; } }
        else if (mando === null) return null;                 // un número antes de ningún comando
        else if (mando === 'M') mando = 'L';                  // repetición implícita de M = L (SVG)
        else if (mando === 'Z') return null;                  // números tras cerrar: sin sentido

        if (mando === 'M') {
            const v = leer(2); if (!v) return null;
            vistos++;
            if (vistos > 1) return null;
            cur = { x: v[0], y: v[1] };
        } else if (mando === 'L') {
            const v = leer(2); if (!v || !cur) return null;
            const p1 = { x: v[0], y: v[1] };
            tramos.push({ tipo: 'L', p0: cur, p1: p1 });
            cur = p1;
        } else if (mando === 'Q') {
            const v = leer(4); if (!v || !cur) return null;
            const p1 = { x: v[2], y: v[3] };
            tramos.push({ tipo: 'Q', p0: cur, c1: { x: v[0], y: v[1] }, p1: p1 });
            cur = p1;
        } else if (mando === 'C') {
            const v = leer(6); if (!v || !cur) return null;
            const p1 = { x: v[4], y: v[5] };
            tramos.push({ tipo: 'C', p0: cur, c1: { x: v[0], y: v[1] }, c2: { x: v[2], y: v[3] }, p1: p1 });
            cur = p1;
        }
        // La `Z` NO añade tramo: cierra por el borde INFERIOR del viewBox, que no es borde
        // entre las dos secciones sino el suelo del parche.
    }
    return tramos.length ? tramos : null;
}

function _evaluar(tramo, t) {
    const u = 1 - t;
    if (tramo.tipo === 'Q') {
        return {
            x: u * u * tramo.p0.x + 2 * u * t * tramo.c1.x + t * t * tramo.p1.x,
            y: u * u * tramo.p0.y + 2 * u * t * tramo.c1.y + t * t * tramo.p1.y,
        };
    }
    if (tramo.tipo === 'C') {
        return {
            x: u * u * u * tramo.p0.x + 3 * u * u * t * tramo.c1.x + 3 * u * t * t * tramo.c2.x + t * t * t * tramo.p1.x,
            y: u * u * u * tramo.p0.y + 3 * u * u * t * tramo.c1.y + 3 * u * t * t * tramo.c2.y + t * t * t * tramo.p1.y,
        };
    }
    return { x: tramo.p0.x + (tramo.p1.x - tramo.p0.x) * t, y: tramo.p0.y + (tramo.p1.y - tramo.p0.y) * t };
}

function _medio(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }

/** Un tramo curvo como CÚBICA [p0,c1,c2,p1]. La Q se eleva de grado (conversión EXACTA: la
 *  curva es la misma), así que hay un solo partidor y un solo criterio, no dos. */
function _aCubica(tr) {
    if (tr.tipo === 'C') return [tr.p0, tr.c1, tr.c2, tr.p1];
    const c = tr.c1;
    return [tr.p0,
            { x: tr.p0.x + 2 / 3 * (c.x - tr.p0.x), y: tr.p0.y + 2 / 3 * (c.y - tr.p0.y) },
            { x: tr.p1.x + 2 / 3 * (c.x - tr.p1.x), y: tr.p1.y + 2 / 3 * (c.y - tr.p1.y) },
            tr.p1];
}

/** Giro TOTAL (grados) del polígono que forman los puntos: suma de lo que se dobla en cada
 *  vértice. Los lados de longitud 0 se saltan (un control pegado a su extremo no dobla nada). */
function _giroPoligono(pts) {
    let total = 0, ax = 0, ay = 0, hay = false;
    for (let i = 1; i < pts.length; i++) {
        const bx = pts[i].x - pts[i - 1].x, by = pts[i].y - pts[i - 1].y;
        const nb = Math.sqrt(bx * bx + by * by);
        if (nb < 1e-9) continue;
        if (hay) {
            let cos = (ax * bx + ay * by) / (Math.sqrt(ax * ax + ay * ay) * nb);
            cos = Math.min(1, Math.max(-1, cos));
            total += Math.acos(cos) * 180 / Math.PI;
        }
        ax = bx; ay = by; hay = true;
    }
    return total;
}

/**
 * ¿La cuerda p0→p1 representa ya bien esta cúbica? Las dos cotas se miden sobre el POLÍGONO DE
 * CONTROL, no sobre el punto de en medio: una curva en S tiene el punto medio JUSTO encima de
 * la cuerda (sagita 0) y se sale por los dos lados — medido, el criterio del punto medio dejaba
 * la Ola Suave con un error de 5,8 px. El polígono de control acota las dos cosas por propiedad
 * de la envolvente convexa, así que nunca se le escapa un bulto.
 *   · POSICIÓN: la desviación de una cúbica respecto a su cuerda no pasa de 3/4 de la distancia
 *     del control más lejano (cota estándar de la envolvente convexa).
 *   · FORMA: la curva gira siempre MENOS que su polígono de control (propiedad de disminución
 *     de la variación), así que acotar el del polígono acota el codo del vértice.
 */
function _cuerdaBasta(q, sagitaMax, giroMax) {
    const p0 = q[0], p1 = q[3];
    const cx = p1.x - p0.x, cy = p1.y - p0.y;
    const base = Math.sqrt(cx * cx + cy * cy);
    let lejos = 0;
    for (let i = 1; i <= 2; i++) {
        const dx = q[i].x - p0.x, dy = q[i].y - p0.y;
        const d = base > 1e-9 ? Math.abs(dx * cy - dy * cx) / base : Math.sqrt(dx * dx + dy * dy);
        if (d > lejos) lejos = d;
    }
    if (0.75 * lejos > sagitaMax) return false;
    return _giroPoligono(q) <= giroMax;
}

/** Parte la cúbica por la mitad (de Casteljau, exacto) → [izquierda, derecha]. */
function _partirEnDos(q) {
    const a = _medio(q[0], q[1]), b = _medio(q[1], q[2]), c = _medio(q[2], q[3]);
    const d = _medio(a, b), e = _medio(b, c), f = _medio(d, e);
    return [[q[0], a, d, f], [f, e, c, q[3]]];
}

/**
 * Parte una cúbica en cuerdas y las empuja en `salida` (sin el punto inicial, que ya está).
 * Mientras la cuerda no baste (ver `_cuerdaBasta`) se parte en dos y se vuelve a preguntar por
 * cada mitad: así los puntos acaban DONDE EL PATH GIRA y no repartidos por el ancho — el trozo
 * casi recto sale con UNA cuerda exacta y la cresta con seis.
 */
function _partirCubica(q, salida, prof, sagitaMax, giroMax) {
    if (prof < MUESTRAS_PROF_MAX && !_cuerdaBasta(q, sagitaMax, giroMax)) {
        const mitades = _partirEnDos(q);
        _partirCubica(mitades[0], salida, prof + 1, sagitaMax, giroMax);
        _partirCubica(mitades[1], salida, prof + 1, sagitaMax, giroMax);
        return;
    }
    salida.push({ x: q[3].x, y: q[3].y });
}

/** Muestreo ADAPTATIVO por curvatura: los puntos van donde el path gira (ver `_partirCubica`). */
function _muestreoAdaptativo(tramos, sagitaMax, giroMax) {
    const crudos = [{ x: tramos[0].p0.x, y: tramos[0].p0.y }];
    tramos.forEach((tr) => {
        if (tr.tipo === 'L') { crudos.push({ x: tr.p1.x, y: tr.p1.y }); return; }
        _partirCubica(_aCubica(tr), crudos, 0, sagitaMax, giroMax);
    });
    return crudos;
}

/** Muestreo de presupuesto FIJO repartido por el ANCHO de cada tramo curvo (uniforme en t).
 *  Es el reparto histórico; sigue siendo la manera de pedir una referencia densa de la curva. */
function _muestreoFijo(tramos, presupuesto) {
    let anchoCurvas = 0;
    tramos.forEach((tr) => { if (tr.tipo !== 'L') anchoCurvas += Math.abs(tr.p1.x - tr.p0.x); });

    const crudos = [{ x: tramos[0].p0.x, y: tramos[0].p0.y }];
    tramos.forEach((tr) => {
        if (tr.tipo === 'L') { crudos.push({ x: tr.p1.x, y: tr.p1.y }); return; }
        const cuota = anchoCurvas > 0
            ? Math.round(presupuesto * Math.abs(tr.p1.x - tr.p0.x) / anchoCurvas)
            : presupuesto;
        const pasos = Math.max(MUESTRAS_MIN_TRAMO, cuota);
        for (let k = 1; k <= pasos; k++) crudos.push(_evaluar(tr, k / pasos));
    });
    return crudos;
}

/**
 * Convierte los tramos en la POLILÍNEA DEL BORDE: la línea que separa lo que es sección A de
 * lo que es sección B, de izquierda a derecha.
 *
 * Cómo se separa el borde del resto del contorno: las formas del catálogo son polígonos
 * CERRADOS que rellenan desde la curva hasta el suelo del viewBox, y ese contorno se escribe
 * siempre igual — primero el borde (de izquierda a derecha) y luego el cierre, que vuelve hacia
 * atrás (`L1440,80 L0,80 Z` y compañía). Así que el borde es el PREFIJO en el que la x no
 * retrocede: en cuanto un punto va hacia la izquierda, empieza el cierre y se descarta. Los
 * tramos verticales (los escalones, `M0,80 L0,60`) sobreviven porque su x no retrocede, y quedan
 * como aristas verticales del polígono — que es justo lo que dibuja una escalera.
 *
 * @param {Array} tramos  los de `parsearPathDivider`
 * @param {number} [n]    presupuesto FIJO de muestras para los tramos curvos (reparto por ancho).
 *                        Sin él —lo normal— el muestreo es ADAPTATIVO por curvatura.
 * @returns {Array<{x:number,y:number}>|null} puntos en unidades del viewBox (x 0-1440, y 0-80)
 */
export function muestrearPath(tramos, n) {
    if (!Array.isArray(tramos) || !tramos.length) return null;

    let crudos;
    if (n > 0) {
        crudos = _muestreoFijo(tramos, n);
    } else {
        // Adaptativo. Si una forma rarísima (un `custom_path` con decenas de curvas) se pasara
        // del presupuesto, se afloja la tolerancia y se repite: el `polygon()` viaja en el HTML
        // de CADA página y no puede crecer sin límite. Determinista y acotado a 4 aflojadas.
        let sagita = TOL_SAGITA;
        let giro = TOL_GIRO;
        crudos = _muestreoAdaptativo(tramos, sagita, giro);
        for (let k = 0; k < 4 && crudos.length > MUESTRAS_MAX; k++) {
            sagita *= 2;
            giro *= 2;
            crudos = _muestreoAdaptativo(tramos, sagita, giro);
        }
        // RESPALDO, y hace falta: aflojar la tolerancia NO baja del suelo de un punto por tramo,
        // así que las 4 vueltas de arriba no acotan nada cuando lo que dispara los puntos es el
        // número de curvas. MEDIDO sobre `custom_path` sintéticos (20/60/130 tramos `C` de
        // curvatura fuerte): 120/241/520 puntos de adaptativo contra 61/181/391 del reparto fijo
        // de antes — o sea que en ESE caso el adaptativo engordaba el `polygon()` por encima de
        // lo que había. Aquí se emite el más BARATO de los dos, así que el peor caso pasa a ser
        // el de siempre y el presupuesto se respeta mientras sea geométricamente posible.
        // No toca ni una forma del catálogo (la más cara son 85 puntos), solo el patológico.
        if (crudos.length > MUESTRAS_MAX) {
            const fijo = _muestreoFijo(tramos, MUESTRAS_MAX);
            if (fijo.length < crudos.length) crudos = fijo;
        }
    }

    // Prefijo con x no decreciente = el borde (ver el comentario de arriba).
    const borde = [];
    for (let k = 0; k < crudos.length; k++) {
        if (k > 0 && crudos[k].x < crudos[k - 1].x - 1e-6) break;
        const x = Math.min(VB_W, Math.max(0, crudos[k].x));
        const y = Math.min(VB_H, Math.max(0, crudos[k].y));
        const prev = borde[borde.length - 1];
        if (prev && Math.abs(prev.x - x) < 1e-6 && Math.abs(prev.y - y) < 1e-6) continue;  // duplicado
        borde.push({ x: x, y: y });
    }
    // Los tramos VERTICALES del principio y del final son los cantos izquierdo y derecho del
    // contorno cerrado, no borde: `M0,80 L0,60 …` sube por el lateral antes de empezar, y
    // `… L1440,52 L1440,80` baja por el otro al terminar. Se colapsa cada run a su punto útil
    // (el ÚLTIMO del inicial, el PRIMERO del final). Los verticales de EN MEDIO se conservan:
    // son los peldaños de "Escalones".
    while (borde.length > 1 && Math.abs(borde[0].x - borde[1].x) < 1e-6) borde.shift();
    while (borde.length > 1 && Math.abs(borde[borde.length - 1].x - borde[borde.length - 2].x) < 1e-6) borde.pop();
    if (borde.length < 2) return null;
    // El borde tiene que cruzar la sección ENTERA: si el path empieza o acaba antes, se
    // prolonga en horizontal (un custom_path corto no debe abrir un agujero en el lateral).
    if (borde[0].x > 0) borde.unshift({ x: 0, y: borde[0].y });
    if (borde[borde.length - 1].x < VB_W) borde.push({ x: VB_W, y: borde[borde.length - 1].y });
    return borde;
}


// ─────────────────────────────────────────────────────────────
//  El polígono del `clip-path`
// ─────────────────────────────────────────────────────────────

function _redondear(n) {
    const v = Math.round(n * 100) / 100;
    return String(v === 0 ? 0 : v);   // -0 → 0
}

function _pctX(x) {
    return _redondear(x / VB_W * 100) + '%';
}

// Cuánto se pasa la sección de ARRIBA por debajo de la curva para que las dos no se encuentren
// en la misma línea exacta (ver el porqué en `poligonoRecorte`). En píxeles de CSS: con 0 vuelve
// la costura por la que se ve el fondo de la página.
const RECORTE_SOLAPE = 1;

/** y del viewBox (0-80) → px dentro de la banda de alto `h`. */
function _yPx(y, h) {
    return Math.min(h, Math.max(0, y / VB_H * h));
}

// El borde muestreado de cada `d` es DETERMINISTA: se cachea (una página repinta el mismo
// catálogo decenas de veces por render, y cada muestreo son ~70 evaluaciones de bézier).
const _BORDES = new Map();
function _bordeDe(d) {
    if (_BORDES.has(d)) return _BORDES.get(d);
    if (_BORDES.size > 256) _BORDES.clear();      // custom_path distintos sin límite: no acumular
    const tramos = parsearPathDivider(d);
    const puntos = tramos ? muestrearPath(tramos) : null;   // sin `n` = adaptativo por curvatura
    _BORDES.set(d, puntos);
    return puntos;
}

/**
 * Polígono del `clip-path` de UNA sección, con sus dos bordes posibles en el MISMO polígono
 * (una sección puede tener recorte arriba —el separador que viene de la anterior— y abajo —el
 * suyo hacia la siguiente—; `clip-path` solo admite uno, así que se combinan).
 *
 * Admite las dos formas de llamada:
 *   · un solo lado:  `poligonoRecorte({ puntos, h, lado: 'abajo' })`
 *   · los dos:       `poligonoRecorte({ arriba: {puntos,h}, abajo: {puntos,h} })`
 *
 * Arriba se expresa en px desde el borde superior; abajo, desde el inferior (`100% - 47.5px`),
 * porque el alto de la sección no se conoce hasta que pinta.
 *
 * Las dos bandas se ACOTAN para que el polígono nunca se cruce a sí mismo: en una sección más
 * baja que sus bandas (una tira `logos` compacta de 150 px con dos separadores de 140 px), los
 * puntos de arriba no pasan del suelo (`min(y, 100%)`) y los de abajo no suben por encima de la
 * banda de arriba (`max(100% - d, H_arriba)`). Un polígono cruzado se pinta como una pajarita
 * con la franja central desaparecida, y además sin clics (clip-path recorta el hit-testing).
 *
 * Por el lado que NO se recorta, el polígono sale 1 px POR FUERA de la caja: ahí puede colgar un
 * parche de siempre (`bottom:-1px` / `top:-1px`) y ese voladizo antisubpíxel tiene que vivir.
 *
 * LAS DOS CURVAS SOLAPAN 1 px, y no es un detalle cosmético: sin solape los dos recortes se
 * encuentran EXACTAMENTE en la misma línea, así que en el píxel del borde la de arriba cubre α
 * y la de abajo 1-α — y como se pintan una sobre otra, el fondo de la PÁGINA se cuela con peso
 * α(1-α), hasta un 25 % en el píxel medio. En un borde casi horizontal (la banda mide 60 px
 * sobre 2500 de ancho) esa costura no es un punto: es una tira larga de píxeles aclarados que
 * se lee como un corte de sierra. El solape lo pone la de ARRIBA, que se queda 1 px POR DEBAJO
 * de la curva: la de abajo se pinta después y encima, así que el borde que se VE sigue siendo
 * exactamente el mismo —no se mueve ni un píxel— pero ya se funde contra un color opaco.
 * ESA garantía descansa en el ORDEN DEL DOM, y por eso vale en el sitio PUBLICADO y en el
 * editor en reposo, pero NO mientras el ratón está sobre la sección de ARRIBA: `editor.css`
 * sube su wrapper a `z-index: 20` (11 el seleccionado), o sea que pasa a pintarse ENCIMA de la
 * de abajo y su píxel de solape tapa el borde. Medido en Chrome: la línea baja exactamente
 * 1 px mientras dura el hover y vuelve al salir. Es cosmético y solo del editor; si alguna vez
 * molesta, `RECORTE_SOLAPE = 0` devuelve el comportamiento anterior (con su costura).
 *
 * @returns {string} `polygon(...)`
 */
export function poligonoRecorte(opciones) {
    const o = opciones || {};
    const lados = o.puntos
        ? (o.lado === 'arriba' ? { arriba: o } : { abajo: o })
        : o;
    const arriba = lados.arriba && lados.arriba.puntos ? lados.arriba : null;
    const abajo = lados.abajo && lados.abajo.puntos ? lados.abajo : null;
    const arr = [];

    if (arriba) {
        arriba.puntos.forEach((p) => {
            const y = _yPx(p.y, arriba.h);
            arr.push(_pctX(p.x) + ' ' + (y <= 0 ? '0' : 'min(' + _redondear(y) + 'px, 100%)'));
        });
    } else {
        arr.push('0 -1px', '100% -1px');
    }

    if (abajo) {
        const suelo = arriba ? _redondear(arriba.h) + 'px' : '0px';
        for (let i = abajo.puntos.length - 1; i >= 0; i--) {
            const p = abajo.puntos[i];
            // `- RECORTE_SOLAPE`: esta sección se queda 1 px por DEBAJO de la curva para que la
            // de abajo (que se pinta encima) no funda su borde contra el fondo de la página.
            const d = abajo.h - _yPx(p.y, abajo.h) - RECORTE_SOLAPE;
            arr.push(_pctX(p.x) + ' ' + (d <= 0 ? '100%' : 'max(100% - ' + _redondear(d) + 'px, ' + suelo + ')'));
        }
    } else {
        arr.push('100% calc(100% + 1px)', '0 calc(100% + 1px)');
    }

    return 'polygon(' + arr.join(', ') + ')';
}


// ─────────────────────────────────────────────────────────────
//  La decisión: ¿hay separador? ¿recorte o parche?
// ─────────────────────────────────────────────────────────────

/**
 * ÚNICA regla del proyecto sobre si dos secciones llevan separador y de qué clase.
 *
 * @param {Object} bloqueA  la sección de ARRIBA (la que tiene `props.section_divider`)
 * @param {Object} bloqueB  la de abajo
 * @returns {Object} {modo:'none'} | {modo:'parche', h, cfg, tipo, color, preconversion}
 *                                 | {modo:'recorte', h, cfg, tipo, puntos, flip}
 */
export function decidirDivider(bloqueA, bloqueB) {
    const NINGUNO = { modo: 'none' };
    if (!bloqueA || !bloqueB) return NINGUNO;

    const pA = bloqueA.props || {};
    const pB = bloqueB.props || {};
    const tipo = pA.section_divider;
    if (!tipo || tipo === 'none') return NINGUNO;
    if (DIVIDER_EXCLUDE_TYPES.indexOf(bloqueA.type) >= 0) return NINGUNO;

    const cfg = pA.divider_config || {};
    const hayConfig = Object.keys(cfg).length > 0;
    // Condición histórica: separador si los fondos DIFIEREN o si el cliente lo configuró a mano
    // (entre dos fondos idénticos el parche era invisible; el recorte, además, un no-op).
    if (pA.bgColor === pB.bgColor && !hayConfig) return NINGUNO;

    const preconversion = PRECONVERSION_TYPES.indexOf(bloqueB.type) >= 0;
    const h = _altura(cfg.height, preconversion);
    const opacidad = _opacidad(cfg.opacity);
    const customPath = _pathSeguro(cfg.custom_path);   // basura → como si no lo hubiera
    const parche = {
        modo: 'parche', h: h, cfg: cfg, tipo: tipo,
        color: _colorSeguro(cfg.color) || _colorSeguro(pB.bgColor) || 'transparent',
        opacidad: opacidad,
        custom_path: customPath,
        preconversion: preconversion,
    };

    // ── Lo que un recorte NO puede expresar → parche (el SVG de siempre, byte a byte) ──
    // Un color FIJO (o Accent) no es un borde entre A y B: es una franja de un TERCER color.
    if (cfg.color) return parche;
    // Una opacidad parcial es una MEZCLA de los dos colores; recortar es todo o nada.
    if (opacidad < 100) return parche;
    // "Arriba" cuelga el SVG del borde SUPERIOR de A: no es el borde entre A y B.
    if (cfg.invert) return parche;
    // `flip` espeja el SVG entero (`scaleY(-1)`): en el parche no solo se da la vuelta la curva,
    // se da la vuelta QUÉ LADO va relleno — la cuña del color de B cuelga del techo de la banda
    // con A por debajo. Un recorte solo sabe poner A arriba y B abajo; espejar la curva sola
    // dejaría el separador en el lado contrario al que siempre tuvo.
    if (cfg.flip) return parche;
    // La "respiración" (y el parallax/morph) ANIMAN el SVG; un clip-path estático no respira.
    if (cfg.animated) return parche;
    // B con alto fijo o sin `<section>` de raíz (ver RECORTE_ARRIBA_EXCLUDE).
    if (RECORTE_ARRIBA_EXCLUDE.indexOf(bloqueB.type) >= 0) return parche;

    const capas = customPath
        ? [{ d: customPath, opacity: 1 }]
        : getDividerPaths(tipo);
    if (!customPath) {
        const entrada = DIVIDER_CATALOG[tipo];
        if (entrada && entrada.motion) return parche;   // wave-parallax / wave-morph: los mueve el JS
    }
    // Formas de varias capas con alguna TRANSLÚCIDA (Ola Doble, Doble Corte): la capa al 40-50 %
    // es una mezcla, igual que `opacity` — se pintan con el parche y siguen exactamente igual.
    for (let i = 0; i < capas.length; i++) {
        const op = capas[i].opacity;
        if ((op === undefined || op === null ? 1 : op) < 1) return parche;
    }

    const puntos = _bordeDe(capas[capas.length - 1].d);
    if (!puntos) return parche;                          // custom_path con comandos que no leemos

    return { modo: 'recorte', h: h, cfg: cfg, tipo: tipo, puntos: puntos };
}

/**
 * Los DOS separadores que le atañen a la sección `index` —el que viene de la anterior y el suyo
 * hacia la siguiente— decididos UNA sola vez, más el recorte que resulta. Lo consumen la
 * inyección en el HTML y el wrapper del editor (que llevan LOS MISMOS atributos): render.js lo
 * calcula una vez por bloque y se lo pasa a `inyectarDivider` para que no lo recalcule.
 *
 * @returns {{arriba:Object, abajo:Object, recorte:(Object|null)}}
 *          `recorte` = {lados:['arriba','abajo'], atributo, vars, estilo} o null si no hay
 */
/** El «no hay separador» explícito. Se exporta para que quien no pueda calcularlo lo diga en
 *  vez de pasar `undefined`, que hace que `inyectarDivider` lo recalcule (y vuelva a fallar). */
export const SIN_SEPARADORES = { arriba: { modo: 'none' }, abajo: { modo: 'none' }, recorte: null };

export function separadoresDe(blocks, index) {
    if (!Array.isArray(blocks)) return { arriba: { modo: 'none' }, abajo: { modo: 'none' }, recorte: null };
    const arriba = decidirDivider(blocks[index - 1], blocks[index]);
    const abajo = decidirDivider(blocks[index], blocks[index + 1]);
    return { arriba: arriba, abajo: abajo, recorte: _recorte(arriba, abajo) };
}

/** Solo el recorte de la sección `index` (atajo de `separadoresDe`). */
export function recorteDe(blocks, index) {
    return separadoresDe(blocks, index).recorte;
}

function _recorte(arriba, abajo) {
    const hayArriba = arriba.modo === 'recorte';
    const hayAbajo = abajo.modo === 'recorte';
    if (!hayArriba && !hayAbajo) return null;

    const lados = [];
    if (hayArriba) lados.push('arriba');
    if (hayAbajo) lados.push('abajo');

    const vars = {};
    // DOS variables y no una: una sección puede tener recorte por los dos lados con ALTURAS
    // distintas. `--recorte-h` (arriba) la usan el margen negativo, el hueco y las herramientas
    // del editor; `--recorte-h-abajo`, el botón "+" de insertar sección.
    if (hayArriba) vars['--recorte-h'] = arriba.h + 'px';
    if (hayAbajo) vars['--recorte-h-abajo'] = abajo.h + 'px';
    vars['--recorte-clip'] = poligonoRecorte({
        arriba: hayArriba ? { puntos: arriba.puntos, h: arriba.h } : null,
        abajo: hayAbajo ? { puntos: abajo.puntos, h: abajo.h } : null,
    });

    const estilo = Object.keys(vars).map((k) => k + ':' + vars[k] + ';').join('');
    return { lados: lados, atributo: lados.join(' '), vars: vars, estilo: estilo };
}


// ─────────────────────────────────────────────────────────────
//  Pintado
// ─────────────────────────────────────────────────────────────

/**
 * El SVG del modelo PARCHE. Es el markup histórico BYTE A BYTE (lo fija un golden en
 * `tests/test_dividers_recorte.py`): un sitio que caiga en este modelo se publica idéntico.
 * @param {Object} decision  la que devuelve `decidirDivider` con modo 'parche'
 */
export function dividerParcheHtml(decision) {
    const cfg = decision.cfg || {};
    const height = decision.h;
    const fillColor = decision.color;
    const opacity = (decision.opacidad === undefined ? _opacidad(cfg.opacity) : decision.opacidad) / 100;
    const flip = cfg.flip ? ' transform:scaleY(-1);' : '';
    const position = cfg.invert ? 'top:-1px;' : 'bottom:-1px;';
    const animClass = cfg.animated ? ' divider-animated' : '';
    const extraClass = decision.preconversion ? ' section-divider--preconversion' : '';

    // custom_path (ya saneado por `decidirDivider`) o catálogo
    const customPath = decision.custom_path === undefined ? _pathSeguro(cfg.custom_path) : decision.custom_path;
    const pathsData = customPath
        ? [{ d: customPath, opacity: 1 }]
        : getDividerPaths(decision.tipo);
    // Todo lo interpolado va ESCAPADO: color y path vienen de `divider_config`, que se guarda
    // tal cual desde la API. Sin esto, `divider_config.color = '"><img onerror=…>'` acababa en
    // el innerHTML del editor (el saneador del servidor solo cubre la copia publicada).
    const pathsHtml = pathsData.map(p =>
        `<path d="${_attr(p.d)}" fill="${_attr(fillColor)}" opacity="${_attr(p.opacity)}"/>`
    ).join('');

    return `<svg class="section-divider${extraClass}${animClass}" viewBox="0 0 1440 80" preserveAspectRatio="none" style="${position} height:${height}px;${flip} opacity:${opacity};">${pathsHtml}</svg>`;
}

/**
 * Mete en el HTML de UNA sección todo lo que le toca del separador. La llaman render.js
 * (editor) y viewer.js::compileSiteHtml (publicado): una sola inyección compartida, así que la
 * paridad editor/publicado (invariante nº4) es por CONSTRUCCIÓN y no por copiar el bloque.
 *
 *  · Parche → el `<svg class="section-divider">` justo antes de la etiqueta de cierre (como siempre).
 *  · Recorte → `data-recorte` + las variables FUNDIDAS en el `style="…"` que la etiqueta raíz ya
 *    tiene (nunca un segundo atributo `style`), y el hueco en flujo como primer hijo cuando el
 *    recorte es por arriba.
 *
 * @param {string} blockHtml  el HTML del bloque `index`, con el resto de inyecciones ya hechas
 * @param {Array}  blocks     la página entera (el separador mira a la anterior y a la siguiente)
 * @param {number} index
 * @param {Object} [sep]      lo que devuelve `separadoresDe(blocks, index)` si el caller ya lo
 *                            tiene (render.js lo calcula para el wrapper); si no, se calcula aquí
 * @returns {string}
 */
/**
 * La `<section>` RAÍZ del bloque, saltándose lo que puede ir por delante sin ser contenido:
 * espacios, comentarios y un `<style>` propio (`comparison` abre con uno, y por eso esto no es
 * paranoia: sin saltarlo, su sección no recibiría el recorte y —como la de ARRIBA sí se recorta,
 * la decide otra llamada— quedaría un boquete con el fondo de la página en la cuña).
 * @returns {{i:number, tag:string}|null}
 */
function raizSection(html) {
    let i = 0;
    for (;;) {
        while (i < html.length && /\s/.test(html.charAt(i))) i++;
        if (html.substr(i, 4) === '<!--') {
            const fin = html.indexOf('-->', i);
            if (fin < 0) return null;
            i = fin + 3;
            continue;
        }
        if (/^<style[\s>]/i.test(html.substr(i, 7))) {
            // Regex con `lastIndex`, no `toLowerCase()` del HTML entero (un `comparison` con
            // muchas filas son decenas de KB copiados por cada inyección).
            const cierre = /<\/style\s*>/ig;
            cierre.lastIndex = i;
            const m = cierre.exec(html);
            if (!m) return null;
            i = m.index + m[0].length;
            continue;
        }
        break;
    }
    // El cierre, respetando comillas (`utils/tag.js`): con `[^>]*>` un `>` dentro de un atributo
    // cortaba el tag ahí y el recorte se escribía en mitad de ese valor.
    if (!/^<section\b/.test(html.substr(i, 9))) return null;
    const fin = finDelTag(html, i);
    return fin === -1 ? null : { i: i, tag: html.slice(i, fin + 1) };
}

export function inyectarDivider(blockHtml, blocks, index, sep) {
    if (!blockHtml || !Array.isArray(blocks)) return blockHtml;
    if (!sep) sep = separadoresDe(blocks, index);

    // 1) PARCHE — solo lo emite la sección de ARRIBA del par, igual que siempre.
    const abajo = sep.abajo;
    if (abajo.modo === 'parche') {
        const dividerHtml = dividerParcheHtml(abajo);
        const closingTagMatch = blockHtml.match(/<\/[a-zA-Z0-9]+>\s*$/);
        if (closingTagMatch) {
            blockHtml = blockHtml.substring(0, closingTagMatch.index) + dividerHtml + closingTagMatch[0];
        } else {
            blockHtml += dividerHtml;
        }
    }

    // 2) RECORTE — atributos sobre la raíz + hueco.
    const rec = sep.recorte;
    if (!rec) return blockHtml;
    // Red de seguridad: `decidirDivider` ya descarta por TIPO los bloques cuya raíz no es una
    // <section> (DIVIDER_EXCLUDE_TYPES arriba, RECORTE_ARRIBA_EXCLUDE abajo). Si aun así llega
    // otra cosa, mejor sin recorte que con un clip-path colgado de un tag imprevisto.
    const raiz = raizSection(blockHtml);
    if (!raiz) return blockHtml;

    let tag = raiz.tag;
    // El atributo `style` se localiza exigiendo un ESPACIO delante, no un `\b`: la frontera de
    // palabra también casa detrás de un guion, así que `\bstyle="` encontraba primero el
    // `data-btn-style="` que inyecta compileSiteHtml (y cualquier `data-…-style` futuro) y las
    // variables del recorte habrían acabado DENTRO de ese atributo — pildora → basura, botones
    // sin estilo, y la sección sin `--recorte-clip` (o sea: separador desaparecido en el
    // publicado, intacto en el editor; justo la desincronización que este módulo viene a matar).
    // Hoy el tag lo construye el front con `style` delante, pero el saneador de publicación
    // reordena los atributos ALFABÉTICAMENTE (`data-btn-style` < `style`): basta con que alguien
    // vuelva a pasar HTML ya publicado por aquí para activarlo. Guardián en
    // `tests/test_dividers_recorte.py::Inyeccion::test_no_confunde_data_btn_style_con_style`.
    tag = /\sstyle="/.test(tag)
        ? tag.replace(/(\s)style="/, '$1style="' + rec.estilo)
        : tag.replace(/>$/, ' style="' + rec.estilo + '">');
    tag = tag.replace(/>$/, ` data-recorte="${rec.atributo}">`);

    const hueco = rec.lados.indexOf('arriba') >= 0 ? HUECO_HTML : '';
    return blockHtml.slice(0, raiz.i) + tag + hueco + blockHtml.slice(raiz.i + raiz.tag.length);
}
